import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import FriendlyFlag from "../../components/friendlies/FriendlyFlag";
import { LocationModePill } from "../../components/friendlies/FriendlyPills";
import { friendliesService } from "../../services/friendliesApi";

// "My applications" (design Frame 5). EXACTLY three states — Applied / In chat /
// Request closed — mirrored from the applicant serializer, which structurally
// cannot expose a dismissal. A dismissed application simply keeps showing "Applied"
// while the request is open; it NEVER becomes a rejection signal.
const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const STATE_META = {
  applied: { label: "Applied", bg: "#EEF1F6", fg: "#475467" },
  in_chat: { label: "In chat", bg: "#EAF3FF", fg: "#1866CC" },
  closed: { label: "Request closed", bg: "#F4F6FA", fg: "#8A98AC" },
};

const ApplicationCard = ({ app, onOpenChat }) => {
  const req = app.request || {};
  const club = req.club || {};
  const meta = STATE_META[app.state] || STATE_META.applied;
  return (
    <div className="bg-white rounded-[12px] border border-[#E6EAF0] p-4 flex items-start gap-3" data-testid="my-application-card">
      {club.crest ? (
        <img src={club.crest} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" />
      ) : (
        <span className="w-9 h-9 rounded-full bg-[#0B3E8F] text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">{initials(club.name)}</span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[14px] font-bold text-[#0F1728] truncate">{club.name}</span>
          {req.location?.country && <FriendlyFlag country={req.location.country} />}
        </div>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1 text-[12px] text-[#66707F]">
          <span>{req.team_category}{req.team_label ? ` · ${req.team_label}` : ""}</span>
          <span>· {req.date_phrase}</span>
        </div>
        <div className="mt-1.5"><LocationModePill location={req.location} /></div>
        {app.note && <p className="mt-2 text-[12.5px] italic text-[#475467]">"{app.note}"</p>}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold" style={{ backgroundColor: meta.bg, color: meta.fg }} data-testid={`my-app-state-${app.state}`}>
          {meta.label}
        </span>
        {app.state === "in_chat" && app.channel_cid && (
          <button onClick={() => onOpenChat(app)} className="px-3 py-1.5 rounded-lg border border-[#2283FF] text-[#1866CC] text-[12px] font-semibold hover:bg-[#F0F7FF]">Open chat</button>
        )}
      </div>
    </div>
  );
};

const MyApplicationsPage = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    friendliesService
      .getMyApplications(controller.signal)
      .then((res) => {
        if (!active) return;
        if (res.success) setApps(res.data);
        setLoading(false);
      })
      .catch((e) => {
        // Aborting on unmount (incl. React 18 StrictMode's double-invoke) rejects
        // with CanceledError — expected, not an error to surface.
        if (e?.name !== "CanceledError" && e?.name !== "AbortError" && active) setLoading(false);
      });
    return () => { active = false; controller.abort(); };
  }, []);

  const openChat = (app) => navigate(app.channel_cid ? `/inbox?channel=${app.channel_cid}` : "/inbox");

  return (
    // Platform-standard container (W1-04): no fixed max-width, page scales
    // to 2560+ via the progressive padding ladder.
    <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
      <div className="mb-1 flex items-center gap-2">
        <h1 className="text-[24px] font-bold text-[#14263D]">My applications</h1>
        <ScoutInfoDot id="friendlies-my-apps" title="My applications" body="Where your one-tap applications stand. The posting club starts the chat. You'll find it here and in Chat. 'Request closed' just means the request ended (matched, closed, or expired); it's never a rejection." />
      </div>
      <p className="text-[13px] text-[#5A6B82] mb-5">Where your one-tap applications stand. The posting club starts the chat.</p>

      {loading ? (
        <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-24 rounded-[12px] bg-[#F0F3F7] animate-pulse" />)}</div>
      ) : apps.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
          <p className="text-[15px] font-semibold text-[#14263D]">No applications yet</p>
          <button onClick={() => navigate("/friendlies")} className="mt-3 text-[13px] font-semibold text-[#2283FF] hover:underline">Browse friendlies</button>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map((a) => <ApplicationCard key={a.id} app={a} onOpenChat={openChat} />)}
        </div>
      )}
    </div>
  );
};

export default MyApplicationsPage;
